import type { ApolloError } from '@apollo/client';
import { ActionFunction, replace } from 'react-router-dom';
import { client } from '~/api/client';
import { mutateData, queryData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import {
  ArchiveEmailTemplateDocument,
  ArchiveEmailTemplateMutation,
  ArchiveEmailTemplateMutationVariables,
  CreateEmailTemplateDocument,
  CreateEmailTemplateMutation,
  CreateEmailTemplateMutationVariables,
  type EmailTemplateInput,
  GetEmailTemplateContentDocument,
  GetEmailTemplateContentQuery,
  GetEmailTemplateContentQueryVariables,
  UpdateAgencyEmailGuidelinesDocument,
  UpdateAgencyEmailGuidelinesMutation,
  UpdateAgencyEmailGuidelinesMutationVariables,
  UpdateEmailTemplateDocument,
  UpdateEmailTemplateMutation,
  UpdateEmailTemplateMutationVariables
} from '~/api/graphql';
import { emailTemplatePath } from '~/components/email-templates/email-template-paths';
import {
  visibilityInputFromValue,
  visibilityValueFromFragment
} from '~/components/visibility/visibility-selector.utils';

const getEmailTemplateContent = (id: string) =>
  queryData<
    GetEmailTemplateContentQuery,
    GetEmailTemplateContentQueryVariables,
    GetEmailTemplateContentQuery['emailTemplate']
  >(
    client,
    { query: GetEmailTemplateContentDocument, variables: { id } },
    (data) => data?.emailTemplate
  );

const createEmailTemplate = (variables: CreateEmailTemplateMutationVariables) =>
  mutateData<CreateEmailTemplateMutation, CreateEmailTemplateMutationVariables>(client, {
    mutation: CreateEmailTemplateDocument,
    variables
  });

const updateEmailTemplate = (variables: UpdateEmailTemplateMutationVariables) =>
  mutateData<UpdateEmailTemplateMutation, UpdateEmailTemplateMutationVariables>(client, {
    mutation: UpdateEmailTemplateDocument,
    variables
  });

const archiveEmailTemplate = (variables: ArchiveEmailTemplateMutationVariables) =>
  mutateData<ArchiveEmailTemplateMutation, ArchiveEmailTemplateMutationVariables>(client, {
    mutation: ArchiveEmailTemplateDocument,
    variables
  });

/**
 * Saves a copy under copyName with the same wording, guidelines and audience. A person who may
 * not choose visibility sends none, and the server makes the copy public.
 */
export const duplicateEmailTemplate = async (
  id: string,
  copyName: string,
  { canChooseVisibility }: { canChooseVisibility: boolean }
) => {
  const original = await getEmailTemplateContent(id);
  if (!original.data) {
    return { data: null, error: original.error };
  }
  const { whenToUse, subject, bodyHtml, guidelines, visibility } = original.data;
  const input: EmailTemplateInput = { name: copyName, whenToUse, subject, bodyHtml, guidelines };
  if (canChooseVisibility) {
    input.visibility = visibilityInputFromValue(visibilityValueFromFragment(visibility));
  }
  return createEmailTemplate({ input });
};

// A thrown failure, such as a dropped connection, comes back for the page to explain rather
// than replacing the page and the edits on it.
const asServerError = (serverError: unknown) => ({ serverError: serverError as ApolloError });

export const submitDuplicateEmailTemplate: ActionFunction = async ({ params, request }) => {
  const { copyName, canChooseVisibility } = (await request.json()) as {
    copyName: string;
    canChooseVisibility: boolean;
  };
  return submitMutation(
    duplicateEmailTemplate(params.templateId ?? '', copyName, { canChooseVisibility })
  ).catch(asServerError);
};

/** A new template opens on its own page, so a second save updates it rather than copying it. */
export const submitCreateEmailTemplate: ActionFunction = async ({ request }) => {
  const { input } = (await request.json()) as { input: EmailTemplateInput };
  return submitMutation(createEmailTemplate({ input }), (data) =>
    replace(emailTemplatePath(data?.createEmailTemplate.id ?? ''))
  ).catch(asServerError);
};

export const submitUpdateEmailTemplate: ActionFunction = async ({ params, request }) => {
  const { expectedVersion, input } = (await request.json()) as {
    expectedVersion: number;
    input: EmailTemplateInput;
  };
  return submitMutation(
    updateEmailTemplate({ id: params.templateId ?? '', expectedVersion, input })
  ).catch(asServerError);
};

export const submitArchiveEmailTemplate: ActionFunction = async ({ params }) =>
  submitMutation(archiveEmailTemplate({ id: params.templateId ?? '' })).catch(asServerError);

const updateAgencyEmailGuidelines = (variables: UpdateAgencyEmailGuidelinesMutationVariables) =>
  mutateData<UpdateAgencyEmailGuidelinesMutation, UpdateAgencyEmailGuidelinesMutationVariables>(
    client,
    { mutation: UpdateAgencyEmailGuidelinesDocument, variables }
  );

export const submitAgencyEmailGuidelines: ActionFunction = async ({ request }) =>
  submitMutation(updateAgencyEmailGuidelines(await request.json())).catch(asServerError);
